// src/pages/appointment-calendar/components/WorkingHoursControl.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import WorkingHoursDrawer from "./WorkingHoursDrawer";
import api from "@/lib/api";

/**
 * Buton + onboarding pentru programul de lucru.
 * Dacă nu are locationId în prop sau în localStorage,
 * încearcă să aleagă automat unica locație din /b/locations.
 */

export default function WorkingHoursControl({ locationId, className = "" }) {
  const { t } = useTranslation();
  const [showDrawer, setShowDrawer] = useState(false);
  const [checking, setChecking] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [error, setError] = useState("");
  const [autoLocationId, setAutoLocationId] = useState(null);

  // Citește din localStorage la fiecare render (mai robust când se schimbă business / location)
  const businessId =
    typeof window !== "undefined"
      ? window.localStorage.getItem("current_business_id")
      : null;

  const rawLocation =
    locationId ??
    autoLocationId ??
    (typeof window !== "undefined"
      ? window.localStorage.getItem("current_location_id")
      : null);

  const locId = rawLocation ? Number(rawLocation) || null : null;

  // 1) Dacă nu avem locId dar avem businessId, încercăm să alegem automat locația
  useEffect(() => {
    if (locId || !businessId) return;

    let abort = false;
    async function pickLocation() {
      try {
        const { data } = await api.get("/b/locations");
        if (abort || !Array.isArray(data)) return;

        if (data.length === 1) {
          const only = data[0];
          if (typeof window !== "undefined") {
            window.localStorage.setItem("current_location_id", String(only.id));
          }
          setAutoLocationId(only.id);
        } else if (data.length > 1) {
          setError(t("calendar.selectBranchFirst"));
        }
      } catch (e) {
        if (!abort) {
          console.error("Eroare la /b/locations:", e);
          setError(t("calendar.loadLocationsError"));
        }
      }
    }

    pickLocation();
    return () => {
      abort = true;
    };
  }, [locId, businessId]);

  // 2) Onboarding check: dacă toate zilele sunt închise → needsSetup = true
  useEffect(() => {
    if (!locId) return;
    if (!businessId) {
      setError(t("calendar.noBusinessSelected"));
      return;
    }

    let abort = false;
    async function check() {
      setChecking(true);
      setError("");
      try {
        const { data } = await api.get("/b/working-hours", {
          params: { location_id: locId },
        });
        const rec = data?.recurring || {};
        const allClosed = [0, 1, 2, 3, 4, 5, 6].every((d) => {
          const arr = Array.isArray(rec[String(d)]) ? rec[String(d)] : [];
          return arr.length === 0;
        });
        if (!abort) setNeedsSetup(allClosed);
      } catch (e) {
        if (!abort) {
          console.error("Eroare la /b/working-hours:", e);
          setError(
            e?.response?.data?.detail || t("calendar.checkWorkingHoursError")
          );
        }
      } finally {
        if (!abort) setChecking(false);
      }
    }
    check();
    return () => {
      abort = true;
    };
  }, [locId, businessId]);

  // Stări în care butonul e blocat complet
  if (!locId && !businessId) {
    return (
      <div className={className}>
        <button
          type="button"
          className="rounded border px-3 py-2 text-sm text-gray-400 cursor-not-allowed"
          disabled
          title={t("calendar.noBusinessSelected")}
        >
          {t("calendar.workingHours")}
        </button>
      </div>
    );
  }

  if (!locId && businessId) {
    return (
      <div className={className}>
        <button
          type="button"
          className="rounded border px-3 py-2 text-sm text-gray-400 cursor-not-allowed"
          disabled
          title={t("calendar.selectBranchTitle")}
        >
          {t("calendar.workingHours")}
        </button>
      </div>
    );
  }

  // ------- buton compact, fără bannere sub header --------
  const baseBtn =
    "inline-flex items-center gap-2 rounded border px-3 py-2 text-sm transition-colors";
  const stateClasses = error
    ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
    : needsSetup
    ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
    : "border-gray-300 bg-background text-foreground hover:bg-gray-50";

  const disabledClasses =
    !locId || !businessId || checking ? "opacity-60 cursor-not-allowed" : "";

  const label = needsSetup
    ? t("calendar.workingHoursUnconfigured")
    : t("calendar.workingHours");

  const title =
    error ||
    (!businessId
      ? t("calendar.noBusinessSelected")
      : !locId
      ? t("calendar.selectLocationFirst")
      : t("calendar.configureWorkingHours"));

  return (
    <div className={className}>
      <button
        type="button"
        className={`${baseBtn} ${stateClasses} ${disabledClasses}`}
        onClick={() => setShowDrawer(true)}
        disabled={!locId || !businessId || checking}
        title={title}
      >
        <span>{label}</span>
        {needsSetup && !error && (
          <span
            className="h-2 w-2 rounded-full bg-amber-500"
            aria-hidden="true"
          />
        )}
        {checking && (
          <span className="text-[11px] text-muted-foreground">{t("calendar.checking")}</span>
        )}
      </button>

      {showDrawer && businessId && (
        <WorkingHoursDrawer
          locationId={locId}
          onClose={() => {
            setShowDrawer(false);
            setNeedsSetup(false);
          }}
        />
      )}
    </div>
  );
}
